import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialNormalOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {color} from "@cm/graph-v3"
import {z} from "zod/v4-mini"
import type {Space} from "#material/nodes/vector/normal-map/helpers"
import {spaceSchema} from "#material/nodes/vector/normal-map/helpers"

export interface NormalMapSockets extends MaterialNodeSockets {
    inputs: {
        color: "color" // found in inputs: color,normal; found in settings: color; inferred from data: float3
        strength: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settings: {
        color: "color" // found in inputs: color,normal; found in settings: color; inferred from data: float3
        image: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normal: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        strength: "float" // found in inputs: -; found in settings: float; inferred from data: float
        uvMap: "string" // found in inputs: -; found in settings: string; inferred from data: string
        uvMapIndex: "float" // found in inputs: -; found in settings: integer; inferred from data: float
    }
    settingsExtensions: {
        space?: Space // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        normal: "normal"
    }
}

export const normalMapDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeNormalMap,
    },
    nodeClass: "NormalMap",
    ui: {
        label: "Normal Map",
        color: "#6e5eb2",
        items: [
            {name: "normal", valueType: "normal", label: "Normal", output: true},
            {name: "strength", valueType: "float", label: "Strength", defaultValue: 1, range: {min: 0, max: 10}, input: true, setting: true},
            {name: "color", valueType: "color", label: "Color", input: true, setting: true, defaultValue: {r: 0.5, g: 0.5, b: 1.0}},
            {
                name: "space",
                defaultValue: "TANGENT",
                label: "Space",
                valueType: "string",
                options: [
                    {label: "Tangent", value: "TANGENT"},
                    {label: "Object", value: "OBJECT"},
                    {label: "World", value: "WORLD"},
                    {label: "Blender Object", value: "BLENDER_OBJECT"},
                    {label: "Blender World", value: "BLENDER_WORLD"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: {full: false, remark: "Some parameters are not supported"},
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({normal: materialNormalOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                color: optionalMaterialInput,
                strength: optionalMaterialInput,
            }),
            settings: z.strictObject({
                color: z.optional(color),
                image: z.optional(z.number()),
                normal: z.optional(vec3),
                strength: z.optional(z.number()),
                uvMap: z.optional(z.string()),
                uvMapIndex: z.optional(z.number()),
                space: z.optional(spaceSchema),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<NormalMapSockets>
